import React from "react";
import PageMeta from "@insightfulscience/smart-react/PageMeta";
import { useLocale, WithLocale } from "@insightfulscience/i18";
import {
	PostElementTitleH4,
	PostElementParagraph,
	PostElementListItem,
	PostElementOl,
} from "@insightfulscience/atomic-react/PostElement";
import GuidePageTemplate from "@insightfulscience/atomic-react/GuidePageTemplate";
import GuidePageTemplateMain from "@insightfulscience/atomic-react/GuidePageTemplate/Main";
import Hero from "@insightfulscience/atomic-react/Hero";
import HeroTitle from "@insightfulscience/atomic-react/Hero/Title";
import pipe from "@insightfulscience/shared-utils/fn/pipe";
import SiteLayout from "@src/SiteLayout";
import WithServices from "@src/services";
import resources from "../../locales/legal-terms-of-service-page";

const TermsOfService = () => {
	const [t] = useLocale("legal-terms-of-service-page");
	return (
		<SiteLayout hero>
			<PageMeta title={t("PAGE_TITLE")} description={t("META_DESCRIPTION")}>
				<PageMeta.OGP
					title={t("PAGE_TITLE")}
					description={t("META_DESCRIPTION")}
					locale={t("META_LOCALE")}
					type={t("META_TYPE")}
					url={t("META_URL")}
					image={t("META_IMAGE")}
					imageSecureUrl={t("META_IMAGE_SECURE")}
					siteName={t("META_SITE_NAME")}
					articlePublisher={t("META_ARTICLE_PUBLISHER")}
					isImageBaseUrl
				/>
				<PageMeta.Twitter
					title={t("PAGE_TITLE")}
					description={t("META_DESCRIPTION")}
					site={t("META_TWITTER_SITE")}
					card={t("META_TWITTER_CARD")}
					image={t("META_TWITTER_IMAGE")}
					creator={t("META_TWITTER_CREATOR")}
					isImageBaseUrl
				/>
				<PageMeta.Canonical href={t("META_CANONICAL")} />
				<PageMeta.Author content={t("META_AUTHOR")} />
			</PageMeta>
			<Hero>
				<HeroTitle>{t("TITLE")}</HeroTitle>
			</Hero>
			<GuidePageTemplate sidebar={false}>
				<GuidePageTemplateMain sidebar={false}>
					<PostElementTitleH4>{t("P1")}</PostElementTitleH4>
					<PostElementParagraph>{t("P2")}</PostElementParagraph>
					<PostElementTitleH4>1. {t("UL1")}</PostElementTitleH4>
					<PostElementOl type="decimal">
						<PostElementListItem>{t("UL1_1")}</PostElementListItem>
						<PostElementListItem>{t("UL1_2")}</PostElementListItem>
						<PostElementListItem>{t("UL1_3")}</PostElementListItem>
					</PostElementOl>
					<PostElementTitleH4>2. {t("UL2")}</PostElementTitleH4>
					<PostElementOl type="decimal">
						<PostElementListItem>{t("UL2_1")}</PostElementListItem>
						<PostElementListItem>{t("UL2_2")}</PostElementListItem>
						<PostElementListItem>{t("UL2_3")}</PostElementListItem>
						<PostElementOl type="lower-alpha">
							<PostElementListItem>{t("UL2_3_a")}</PostElementListItem>
							<PostElementListItem>{t("UL2_3_b")}</PostElementListItem>
							<PostElementListItem>{t("UL2_3_c")}</PostElementListItem>
						</PostElementOl>
					</PostElementOl>
					<PostElementTitleH4>3. {t("UL3")}</PostElementTitleH4>
					<PostElementOl type="decimal">
						<PostElementListItem>{t("UL3_1")}</PostElementListItem>
						<PostElementListItem>{t("UL3_2")}</PostElementListItem>
						<PostElementListItem>{t("UL3_3")}</PostElementListItem>
						<PostElementListItem>{t("UL3_4")}</PostElementListItem>
						<PostElementListItem>{t("UL3_5")}</PostElementListItem>
						<PostElementListItem>{t("UL3_6")}</PostElementListItem>
						<PostElementListItem>{t("UL3_7")}</PostElementListItem>
						<PostElementListItem>{t("UL3_8")}</PostElementListItem>
						<PostElementListItem>{t("UL3_9")}</PostElementListItem>
					</PostElementOl>

					<PostElementTitleH4>4. {t("UL4")}</PostElementTitleH4>
					<PostElementOl type="decimal">
						<PostElementListItem>{t("UL4_1")}</PostElementListItem>
						<PostElementListItem>{t("UL4_2")}</PostElementListItem>
						<PostElementListItem>{t("UL4_3")}</PostElementListItem>
						<PostElementListItem>{t("UL4_4")}</PostElementListItem>
						<PostElementListItem>{t("UL4_5")}</PostElementListItem>
						<PostElementListItem>{t("UL4_6")}</PostElementListItem>
						<PostElementListItem>{t("UL4_7")}</PostElementListItem>
						<PostElementListItem>{t("UL4_8")}</PostElementListItem>
						<PostElementListItem>{t("UL4_9")}</PostElementListItem>
						<PostElementListItem>{t("UL4_10")}</PostElementListItem>
						<PostElementListItem>{t("UL4_11")}</PostElementListItem>
						<PostElementListItem>{t("UL4_12")}</PostElementListItem>
					</PostElementOl>
					<PostElementTitleH4>5. {t("UL5")}</PostElementTitleH4>
					<PostElementOl type="decimal">
						<PostElementListItem>{t("UL5_1")}</PostElementListItem>
						<PostElementListItem>{t("UL5_2")}</PostElementListItem>
					</PostElementOl>
					<PostElementTitleH4>6. {t("UL6")}</PostElementTitleH4>
					<PostElementOl type="decimal">
						<PostElementListItem>{t("UL6_1")}</PostElementListItem>
						<PostElementListItem>{t("UL6_2")}</PostElementListItem>
					</PostElementOl>
					<PostElementTitleH4>7. {t("UL7")}</PostElementTitleH4>
					<PostElementOl type="decimal">
						<PostElementListItem>{t("UL7_1")}</PostElementListItem>
						<PostElementListItem>{t("UL7_2")}</PostElementListItem>
					</PostElementOl>
					<PostElementTitleH4>8. {t("UL8")}</PostElementTitleH4>
					<PostElementParagraph>{t("UL8_")}</PostElementParagraph>
					<PostElementTitleH4>9. {t("UL9")}</PostElementTitleH4>
					<PostElementOl type="decimal">
						<PostElementListItem>{t("UL9_1")}</PostElementListItem>
						<PostElementListItem>{t("UL9_2")}</PostElementListItem>
						<PostElementListItem>{t("UL9_3")}</PostElementListItem>
						<PostElementListItem>{t("UL9_4")}</PostElementListItem>
					</PostElementOl>
					<PostElementTitleH4>10. {t("UL10")}</PostElementTitleH4>
					<PostElementParagraph>{t("UL10_")}</PostElementParagraph>
					<PostElementTitleH4>11. {t("UL11")}</PostElementTitleH4>
					<PostElementOl type="decimal">
						<PostElementListItem>{t("UL11_1")}</PostElementListItem>
						<PostElementListItem>{t("UL11_2")}</PostElementListItem>
						<PostElementListItem>{t("UL11_3")}</PostElementListItem>
						<PostElementListItem>{t("UL11_4")}</PostElementListItem>
					</PostElementOl>

					<PostElementTitleH4>12. {t("UL12")}</PostElementTitleH4>
					<PostElementOl type="decimal">
						<PostElementListItem>{t("UL12_1")}</PostElementListItem>
						<PostElementListItem>{t("UL12_2")}</PostElementListItem>
					</PostElementOl>

					<PostElementTitleH4>13. {t("UL13")}</PostElementTitleH4>
					<PostElementParagraph>{t("UL13_")}</PostElementParagraph>

					<PostElementTitleH4>14. {t("UL14")}</PostElementTitleH4>
					<PostElementOl type="decimal">
						<PostElementListItem>{t("UL14_1")}</PostElementListItem>
						<PostElementListItem>{t("UL14_2")}</PostElementListItem>
						<PostElementListItem>{t("UL14_3")}</PostElementListItem>
						<PostElementListItem>{t("UL14_4")}</PostElementListItem>
						<PostElementListItem>{t("UL14_5")}</PostElementListItem>
						<PostElementListItem>{t("UL14_6")}</PostElementListItem>
						<PostElementListItem>{t("UL14_7")}</PostElementListItem>
						<PostElementListItem>{t("UL14_8")}</PostElementListItem>
						<PostElementListItem>{t("UL14_9")}</PostElementListItem>
						<PostElementListItem>{t("UL14_10")}</PostElementListItem>
						<PostElementListItem>{t("UL14_11")}</PostElementListItem>
						<PostElementListItem>{t("UL14_12")}</PostElementListItem>
						<PostElementListItem>{t("UL14_13")}</PostElementListItem>
						<PostElementListItem>{t("UL14_14")}</PostElementListItem>
					</PostElementOl>
					<PostElementParagraph>{t("P3")}</PostElementParagraph>
					<PostElementParagraph>{t("P4")}</PostElementParagraph>
				</GuidePageTemplateMain>
			</GuidePageTemplate>
		</SiteLayout>
	);
};

export default pipe(TermsOfService, WithLocale({ resources }), WithServices);
